/**
 * Social-casino-flavored homepage sections.
 *
 * Six section types seeded by db-setup.ts when siteType === 'social_casino':
 *   - play_now_hero        — full-bleed hero with a "Play Now" CTA, optional
 *                            background video
 *   - how_it_works_steps   — three-step explainer row (sign up → claim coins
 *                            → spin)
 *   - featured_games_grid  — admin-editable tile grid linking deep into the
 *                            embedded game app
 *   - winners_teaser       — recent-winners list. Virtual coin amounts only —
 *                            never displays "$" or real-money values
 *   - responsible_play_badge — small banner: age-rating + hotline link
 *   - app_embed_teaser     — anonymous-visitor wrapper around the AppEmbed
 *                            iframe with a sign-in / play-now overlay
 *
 * Editorial guardrails baked in:
 *   - winners_teaser strips/refuses to render "$" or "USD" prefixes — anything
 *     that could read as cash. Backend seeders pass `amount_coins`, never
 *     `amount`.
 *   - All CTAs link to /play (the embedded app entry) or /signup — never to a
 *     pretend cashier / withdrawal screen.
 */

import { Link } from 'react-router-dom';
import { Coins, ShieldCheck, Sparkles, UserPlus, Gift } from 'lucide-react';
import { analytics } from '@/lib/analytics';

interface SectionProps {
  section: {
    id?: string;
    title?: string | null;
    subtitle?: string | null;
    content?: Record<string, any> | null;
  };
}

// ---------------------------------------------------------------------------
// play_now_hero
// content: { headline, subheadline, cta_label, cta_href, hero_image, bg_video_url }
// ---------------------------------------------------------------------------

export function PlayNowHero({ section }: SectionProps) {
  const c = section.content || {};
  const heroImage: string | null = c.hero_image || null;
  const bgVideo: string | null = c.bg_video_url || null;
  const headline = c.headline || section.title || 'Spin to win';
  const subheadline = c.subheadline || section.subtitle || 'Free coins every day. Play in your browser.';
  const ctaLabel = c.cta_label || 'Play Now';
  const ctaHref = c.cta_href || '/play';

  return (
    <section className="relative overflow-hidden text-white">
      {bgVideo ? (
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={bgVideo}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
      ) : null}
      <div
        className="absolute inset-0 bg-gradient-to-br from-stone-900/80 via-stone-900/70 to-stone-900/90" /* dark-exempt: fixed-dark scrim over hero image */
        style={
          heroImage && !bgVideo
            ? { backgroundImage: `url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : undefined
        }
        aria-hidden="true"
      />
      <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32 text-center">
        <h1 className="text-4xl md:text-6xl font-extrabold mb-4 drop-shadow-lg">
          {headline}
        </h1>
        <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
          {subheadline}
        </p>
        <Link
          to={ctaHref}
          onClick={() => analytics.playClick('hero')}
          className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-cta-deep font-bold text-lg rounded-full hover:bg-amber-300 transition-colors shadow-xl"
        >
          <Sparkles className="w-5 h-5" />
          {ctaLabel}
        </Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// how_it_works_steps
// content: { steps: [{ icon, title, body }] }
// icon is a Lucide name; falls back to a small default catalog.
// ---------------------------------------------------------------------------

interface HowStep { icon?: string; title?: string; body?: string }

const ICONS: Record<string, typeof UserPlus> = {
  UserPlus,
  Gift,
  Sparkles,
  Coins,
  ShieldCheck,
};

export function HowItWorksSteps({ section }: SectionProps) {
  const c = section.content || {};
  const steps: HowStep[] = Array.isArray(c.steps) && c.steps.length > 0
    ? c.steps
    : [
        { icon: 'UserPlus', title: 'Sign up free', body: 'No card required.' },
        { icon: 'Gift', title: 'Claim daily coins', body: 'Log in to collect.' },
        { icon: 'Sparkles', title: 'Spin & win virtual prizes', body: 'Coins, gems, bragging rights.' },
      ];
  return (
    <section className="bg-surface-soft py-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-10">
          {section.title && <h2 className="text-3xl font-bold mb-2">{section.title}</h2>}
          {section.subtitle && <p className="text-text-muted">{section.subtitle}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => {
            const Icon = (s.icon && ICONS[s.icon]) || Sparkles;
            return (
              <div key={i} className="bg-surface rounded-xl shadow-sm border border-line p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 mb-3">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-1">{s.title || `Step ${i + 1}`}</h3>
                {s.body && <p className="text-sm text-text-muted">{s.body}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// featured_games_grid
// content: { games: [{ name, slug, thumbnail, deep_link }] }
// ---------------------------------------------------------------------------

interface FeaturedGame { name?: string; slug?: string; thumbnail?: string; deep_link?: string }

export function FeaturedGamesGrid({ section }: SectionProps) {
  const c = section.content || {};
  const games: FeaturedGame[] = Array.isArray(c.games) ? c.games : [];
  if (games.length === 0) return null;
  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-8">
          {section.title && <h2 className="text-3xl font-bold mb-1">{section.title}</h2>}
          {section.subtitle && <p className="text-text-muted">{section.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {games.map((g, i) => (
            <Link
              key={g.slug || i}
              to={g.deep_link || '/play'}
              onClick={() => analytics.playClick('games-grid', g.slug || g.name)}
              className="group relative aspect-square rounded-xl overflow-hidden bg-surface-muted border border-line hover:border-amber-400 transition-colors"
            >
              {g.thumbnail ? (
                <img
                  src={g.thumbnail}
                  alt={g.name || 'Game thumbnail'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-ink-muted">
                  <Sparkles className="w-10 h-10" />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white font-semibold text-sm">{g.name || 'Featured game'}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// winners_teaser
// content: { winners: [{ display_name, amount_coins, game, when }] }
// IMPORTANT: never renders "$"; values are virtual coin counts.
// ---------------------------------------------------------------------------

interface WinnerRow { display_name?: string; amount_coins?: number; game?: string; when?: string }

function formatCoins(n: number | undefined): string {
  if (typeof n !== 'number' || !isFinite(n)) return '—';
  return `${n.toLocaleString('en-US')} coins`;
}

export function WinnersTeaser({ section }: SectionProps) {
  const c = section.content || {};
  const winners: WinnerRow[] = Array.isArray(c.winners) ? c.winners : [];
  return (
    <section className="py-16 bg-stone-900 text-white" /* dark-exempt: intentional fixed-dark casino section */>
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-6 flex items-baseline justify-between">
          <div>
            {section.title && <h2 className="text-3xl font-bold mb-1">{section.title}</h2>}
            {section.subtitle && <p className="text-white/70 text-sm">{section.subtitle}</p>}
          </div>
          <span className="text-xs text-white/50 uppercase tracking-wider">Virtual coins only</span>
        </div>
        {winners.length === 0 ? (
          <p className="text-white/60 text-center py-8">
            Be the first on the leaderboard — start playing today.
          </p>
        ) : (
          <ul className="divide-y divide-white/10 rounded-xl bg-white/5 border border-white/10">
            {winners.map((w, i) => (
              <li key={i} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/20 text-amber-300 font-bold text-sm">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-medium">{w.display_name || 'Player'}</p>
                    {w.game && <p className="text-xs text-white/60">{w.game}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-amber-300">{formatCoins(w.amount_coins)}</p>
                  {w.when && <p className="text-xs text-white/50">{w.when}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// responsible_play_badge
// content: { min_age, hotline_label, hotline_href }
// ---------------------------------------------------------------------------

export function ResponsiblePlayBadge({ section }: SectionProps) {
  const c = section.content || {};
  const minAge: number = typeof c.min_age === 'number' ? c.min_age : 18;
  const hotlineLabel: string = c.hotline_label || 'Play responsibly';
  const hotlineHref: string | null = c.hotline_href || null;
  return (
    <section className="bg-surface-muted border-y border-line">
      <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ink text-surface text-xs font-bold">
          {minAge}+
        </div>
        <p className="text-sm text-ink-soft">
          <ShieldCheck className="inline w-4 h-4 mr-1 -mt-0.5 text-emerald-600" />
          Play-for-fun only. Virtual coins are not redeemable for cash.
        </p>
        {hotlineHref && (
          <a
            href={hotlineHref}
            className="text-sm text-ink-soft underline decoration-dotted underline-offset-2 hover:text-ink"
          >
            {hotlineLabel}
          </a>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// app_embed_teaser
// Marketing tile for the embedded game app, shown to anonymous visitors. The
// real iframe is rendered route-side (via the post-login redirect) or via a
// separate `app_embed` homepage section for authed admins. We render a styled
// CTA here instead of the iframe so anonymous visitors hit the age gate + auth
// flow first.
// content: { app_slug, cta_label, cta_href }
// ---------------------------------------------------------------------------

export function AppEmbedTeaser({ section }: SectionProps) {
  const c = section.content || {};
  const ctaLabel: string = c.cta_label || 'Play Now';
  const ctaHref: string = c.cta_href || '/play';
  return (
    <section className="py-16 bg-gradient-to-b from-stone-900 to-stone-950 text-white" /* dark-exempt: intentional fixed-dark casino section */>
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-6 text-center">
          {section.title && <h2 className="text-3xl font-bold mb-1">{section.title}</h2>}
          {section.subtitle && <p className="text-white/70 text-sm">{section.subtitle}</p>}
        </div>
        <div className="rounded-2xl border border-amber-400/30 shadow-2xl bg-stone-900/60 p-10 text-center" /* dark-exempt: panel within fixed-dark casino section */>
          <Coins className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-lg text-white/90 mb-6">
            Sign in to claim your daily coin bonus and start spinning.
          </p>
          <Link
            to={ctaHref}
            onClick={() => analytics.playClick('embed-teaser')}
            className="inline-flex items-center gap-2 px-8 py-3 bg-amber-400 text-cta-deep font-bold rounded-full hover:bg-amber-300 transition-colors"
          >
            <Sparkles className="w-5 h-5" />
            {ctaLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}
